import type { AggregatedDataPoint, ReportConfiguration } from '../types/reportTypes';

// ─── CSV Field Escaping (RFC 4180) ──────────────────────────────────────────

/** Spreadsheet apps treat a leading = + - @ as a formula. */
const SPREADSHEET_FORMULA_PREFIX = /^[=+\-@\t\r]/;

/**
 * Escape a single CSV field per RFC 4180.
 * Wraps the field in double quotes if it contains commas, double quotes, or newlines.
 * Any embedded double quotes are doubled ("").
 * Text that starts with a formula character is prefixed with a quote so Excel
 * stores it as text. Numeric values are left alone.
 */
export function escapeCSVField(value: unknown): string {
    let str = value == null ? '' : String(value);
    if (typeof value !== 'number' && SPREADSHEET_FORMULA_PREFIX.test(str)) {
        str = `'${str}`;
    }
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

// ─── generateCSV ────────────────────────────────────────────────────────────

/**
 * Generate a CSV string from column definitions and data rows.
 * Escapes fields per RFC 4180.
 * Optionally prepends metadata rows (report name, date range, filters).
 *
 * Pure function — no side effects.
 */
export function generateCSV(
    columns: { key: string; header: string }[],
    rows: Record<string, any>[],
    metadata?: { reportName: string; dateRange: string; filters: string }
): string {
    const lines: string[] = [];

    // Prepend metadata rows if provided
    if (metadata) {
        lines.push(`Report: ${escapeCSVField(metadata.reportName)}`);
        lines.push(`Date Range: ${escapeCSVField(metadata.dateRange)}`);
        if (metadata.filters) {
            lines.push(`Filters: ${escapeCSVField(metadata.filters)}`);
        }
        lines.push(''); // blank separator line
    }

    // Header row
    lines.push(columns.map((col) => escapeCSVField(col.header)).join(','));

    // Data rows
    for (const row of rows) {
        const fields = columns.map((col) => escapeCSVField(row[col.key]));
        lines.push(fields.join(','));
    }

    return lines.join('\n');
}

// ─── downloadCSV ────────────────────────────────────────────────────────────

/**
 * Create a Blob from the CSV string, generate a temporary URL,
 * trigger a browser download, and revoke the URL.
 */
export function downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// ─── exportReportAsCSV ──────────────────────────────────────────────────────

/**
 * Convenience function that takes AggregatedDataPoint[], builds columns/rows,
 * calls generateCSV, then downloadCSV.
 */
export function exportReportAsCSV(
    data: AggregatedDataPoint[],
    config: ReportConfiguration,
    reportName: string
): void {
    const columns = [
        { key: 'groupLabel', header: 'Group' },
        { key: 'value', header: 'Value' },
        { key: 'count', header: 'Count' },
    ];

    const rows = data.map((dp) => ({
        groupLabel: dp.groupLabel,
        value: dp.value,
        count: dp.count,
    }));

    const dateRange = `${config.dateRangeStart} to ${config.dateRangeEnd}`;
    const filterSummary = config.filters.length > 0
        ? config.filters.map((f) => `${f.fieldLabel} ${f.operator} ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`).join('; ')
        : '';

    const csvContent = generateCSV(columns, rows, {
        reportName,
        dateRange,
        filters: filterSummary,
    });

    const safeFilename = reportName.replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadCSV(csvContent, `${safeFilename}.csv`);
}
